import { Injectable, computed, signal } from '@angular/core';
import { Settings } from 'luxon';

interface TimezoneOverride {
  owner: string;
  zone: string;
}

/**
 * Owns Luxon Settings.defaultZone via ambient (network/group) and entity override layers.
 * Effective zone = top of override stack, else ambient, else system local.
 */
@Injectable({ providedIn: 'root' })
export class TimezoneContextService {
  private readonly ambientZoneSignal = signal<string | null>(null);
  private readonly overrideStackSignal = signal<TimezoneOverride[]>([]);

  readonly ambientZone = this.ambientZoneSignal.asReadonly();
  readonly overrideZone = computed(() => {
    const stack = this.overrideStackSignal();
    return stack.length > 0 ? stack[stack.length - 1].zone : null;
  });
  readonly hasOverride = computed(() => this.overrideStackSignal().length > 0);
  readonly effectiveZone = computed(
    () => this.overrideZone() ?? this.ambientZoneSignal()
  );

  /**
   * Sets the ambient timezone from network / network-group context.
   * Does not clobber an active entity override; effective zone is reapplied.
   */
  setAmbient(zone: string | null): void {
    this.ambientZoneSignal.set(zone || null);
    this.applyEffective();
  }

  /**
   * Pushes an entity-scoped timezone override (e.g. route plan).
   * Replaces any existing override for the same owner.
   */
  pushOverride(owner: string, zone: string): void {
    if (!owner || !zone) {
      return;
    }
    this.overrideStackSignal.update((stack) => [
      ...stack.filter((entry) => entry.owner !== owner),
      { owner, zone },
    ]);
    this.applyEffective();
  }

  /**
   * Removes an entity override by owner. Idempotent if owner is not on the stack.
   */
  popOverride(owner: string): void {
    if (!owner) {
      return;
    }
    const before = this.overrideStackSignal();
    const after = before.filter((entry) => entry.owner !== owner);
    if (after.length === before.length) {
      return;
    }
    this.overrideStackSignal.set(after);
    this.applyEffective();
  }

  private applyEffective(): void {
    const zone = this.effectiveZone();
    Settings.defaultZone = zone ?? 'system';
  }
}
